import { useState, useEffect, useCallback, useRef } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';
import { addToastAtom } from '../../../atoms/v0';
import i18n from 'i18next';

// 获取浏览器原生的 SpeechRecognition API
const BrowserSpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;

// 语言映射表，将 i18n 语言代码映射到 SpeechRecognition 支持的语言代码
const languageMap: Record<string, string> = {
  'zh': 'zh-CN',
  'en': 'en-US',
  'ru': 'ru-RU',
  // 可以根据需要添加更多语言
};

/**
 * Web Speech API 语音识别钩子
 */
export const useWebSpeechRecognition = () => {
  const { t } = useTranslation(['v0/chat']);
  const [isListening, setIsListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const microphone = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioDataArray = useRef<Uint8Array | null>(null);
  const animationFrameId = useRef<number | null>(null);
  const addToast = useSetAtom(addToastAtom);

  // 自动重启标志
  const [autoRestart, setAutoRestart] = useState(false);
  const autoRestartTimeoutRef = useRef<number | null>(null);

  // 使用 react-speech-recognition 提供的钩子
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition({
    transcribing: true,
    clearTranscriptOnListen: true,
    commands: [],
    // 添加回调函数
    onResult: (result: any) => {
      console.log('Speech recognition result:', result);
    },
    onEnd: () => {
      console.log('Speech recognition ended');

      // 检查是否需要自动重启
      if (autoRestart && !listening) {
        console.log('Auto-restarting speech recognition...');

        // 清除之前的超时
        if (autoRestartTimeoutRef.current) {
          clearTimeout(autoRestartTimeoutRef.current);
        }

        // 设置一个短暂的延迟后重新启动
        autoRestartTimeoutRef.current = window.setTimeout(() => {
          console.log('Executing auto-restart...');
          SpeechRecognition.startListening({
            continuous: true,
            language: languageMap[i18n.language.split('-')[0]] || 'en-US',
            interimResults: true
          }).catch((error: any) => {
            console.error('Error auto-restarting speech recognition:', error);
          });
        }, 300);
      }
    },
    onStart: () => {
      console.log('Speech recognition started from callback');
    },
    onError: (event: any) => {
      console.error('Speech recognition error:', event);
    }
  });

  // 分析音频电平
  const analyzeAudio = useCallback(() => {
    if (!analyser.current || !audioDataArray.current) return;

    analyser.current.getByteFrequencyData(audioDataArray.current);
    const average = audioDataArray.current.reduce((sum, value) => sum + value, 0) / audioDataArray.current.length;
    setAudioLevel(average);

    // 打印音频电平，便于调试
    if (average > 10) {
      console.log('Audio level:', average);
    }

    animationFrameId.current = requestAnimationFrame(analyzeAudio);
  }, []);

  // 同步内部状态和库状态
  useEffect(() => {
    setIsListening(listening);
  }, [listening]);

  // 开始语音识别
  const startListening = useCallback(async () => {
    if (!browserSupportsSpeechRecognition) {
      addToast({
        title: t('v0/chat:speechRecognitionNotSupported'),
        description: t('v0/chat:speechRecognitionNotSupportedDescription'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!isMicrophoneAvailable) {
      addToast({
        title: t('v0/chat:microphoneNotAvailable'),
        description: t('v0/chat:microphoneNotAvailableDescription'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      console.log('Starting speech recognition...');
      resetTranscript();

      // 获取麦克风权限并设置音频分析
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        // 创建音频上下文和分析器
        audioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        analyser.current = audioContext.current.createAnalyser();
        microphone.current = audioContext.current.createMediaStreamSource(stream);

        // 连接麦克风到分析器
        microphone.current.connect(analyser.current);

        // 设置分析器参数
        analyser.current.fftSize = 256;
        const bufferLength = analyser.current.frequencyBinCount;
        audioDataArray.current = new Uint8Array(bufferLength);

        // 开始分析音频
        analyzeAudio();

        console.log('Audio analysis started');
      } catch (audioError) {
        console.error('Failed to initialize audio analysis:', audioError);
      }

      // 获取当前语言
      const currentLang = i18n.language.split('-')[0]; // 提取主要语言代码，例如 'zh-CN' -> 'zh'
      const speechRecognitionLang = languageMap[currentLang] || 'en-US'; // 默认使用英语
      console.log('Current language:', currentLang, 'Speech recognition language:', speechRecognitionLang);

      // 更新语言代码显示
      setCurrentLangCode(currentLang.toUpperCase());

      // 启动语音识别
      console.log('About to call SpeechRecognition.startListening()...');
      try {
        // 启用自动重启
        setAutoRestart(true);

        await SpeechRecognition.startListening({
          continuous: true,
          language: speechRecognitionLang,
          interimResults: true // 添加中间结果，更实时
        });
        console.log('Speech recognition started successfully with language:', speechRecognitionLang);

        // 检查 SpeechRecognition 的状态
        console.log('SpeechRecognition state:', SpeechRecognition);
        console.log('Is listening:', listening);
      } catch (speechError) {
        console.error('Error in SpeechRecognition.startListening():', speechError);
        throw speechError; // 重新抛出错误以便外层捕获
      }
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      addToast({
        title: t('v0/chat:speechRecognitionFailed'),
        description: t('v0/chat:speechRecognitionFailedDescription'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [browserSupportsSpeechRecognition, isMicrophoneAvailable, resetTranscript, addToast, t, analyzeAudio]);

  // 停止语音识别
  const stopListening = useCallback(() => {
    console.log('Stopping speech recognition...');

    // 禁用自动重启
    setAutoRestart(false);

    // 清除自动重启超时
    if (autoRestartTimeoutRef.current) {
      clearTimeout(autoRestartTimeoutRef.current);
      autoRestartTimeoutRef.current = null;
    }

    try {
      // 尝试停止语音识别
      SpeechRecognition.stopListening();

      // iOS 兼容性处理：手动设置状态
      if (listening) {
        console.log('Manually setting listening state to false for iOS compatibility');
        // 在 iOS 上，可能需要手动设置状态
        setIsListening(false);
      }
    } catch (error) {
      console.error('Error stopping speech recognition:', error);
      // 即使出错，也要确保状态被重置
      setIsListening(false);
    }

    // 停止音频分析
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    // 断开麦克风连接
    if (microphone.current) {
      try {
        microphone.current.disconnect();
        // 在 iOS 上，可能需要停止所有音轨
        if (microphone.current.mediaStream || (microphone.current as any).stream) {
          const stream = (microphone.current as any).mediaStream || (microphone.current as any).stream;
          if (stream && typeof stream.getTracks === 'function') {
            const tracks = stream.getTracks();
            tracks.forEach((track: MediaStreamTrack) => track.stop());
          }
        }
      } catch (error) {
        console.error('Error disconnecting microphone:', error);
      }
      microphone.current = null;
    }

    // 关闭音频上下文
    if (audioContext.current && audioContext.current.state !== 'closed') {
      audioContext.current.close().catch(console.error);
      audioContext.current = null;
    }

    // 重置音频电平
    setAudioLevel(0);

    console.log('Speech recognition stopped');
  }, [listening, autoRestartTimeoutRef]);

  // 清理函数
  useEffect(() => {
    return () => {
      // 停止音频分析
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      // 断开麦克风连接
      if (microphone.current) {
        microphone.current.disconnect();
      }

      // 关闭音频上下文
      if (audioContext.current && audioContext.current.state !== 'closed') {
        audioContext.current.close().catch(console.error);
      }

      // 清除自动重启超时
      if (autoRestartTimeoutRef.current) {
        clearTimeout(autoRestartTimeoutRef.current);
        autoRestartTimeoutRef.current = null;
      }

      // 禁用自动重启
      setAutoRestart(false);
    };
  }, []);

  // 获取当前语言代码
  const [currentLangCode, setCurrentLangCode] = useState('');

  // 更新当前语言代码
  useEffect(() => {
    const updateLanguageCode = () => {
      const currentLang = i18n.language.split('-')[0]; // 提取主要语言代码，例如 'zh-CN' -> 'zh'
      const speechRecognitionLang = languageMap[currentLang] || 'en-US'; // 默认使用英语
      console.log('Language updated:', currentLang, speechRecognitionLang);
      setCurrentLangCode(currentLang.toUpperCase());
    };

    // 初始化语言代码
    updateLanguageCode();

    // 监听语言变化
    i18n.on('languageChanged', updateLanguageCode);

    // 清理函数
    return () => {
      i18n.off('languageChanged', updateLanguageCode);
    };
  }, []);

  // 打印转录结果，便于调试
  useEffect(() => {
    if (transcript) {
      console.log('Transcript:', transcript);
    }
  }, [transcript]);

  return {
    transcript,
    isListening,
    audioLevel,
    currentLangCode,
    startListening,
    stopListening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  };
};
